import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { type ViewInput, api, apiErrorOf } from "../lib/api";
import { trackGoldGate } from "../lib/gold";
import { detectDeviceKind } from "../lib/playCollection";
import { SiteChrome } from "../components/SiteChrome";
import { GoldDialog } from "../control/components/GoldDialog";

/**
 * "Play this right now" — as a real temporary view on a real device.
 *
 * `/play/random` and `/play/collection/:id` used to build a View in browser memory and
 * hand it to DisplayApp as an `ephemeralView`, bypassing the temporary-view registry and
 * the device table entirely. That made a second way to put a view on a screen, and
 * everything hung off the model was missing from it: no id anyone else could reference, no
 * row in the controller's live list, no way for the remote to retarget it, and — because the
 * only thing distinguishing it was a prop — a hard `sessionsEnabled = !ephemeralView`
 * gate. So the button labelled "Start gooning" landed on the one screen in the app where a
 * goon session could not be started.
 *
 * There is one way now: mint the view, open a device on it, go to that device's screen.
 * The temporary view expires on its own once nothing is watching, and closing the tab
 * takes the device with it, so nothing here is more permanent than the old in-memory
 * version was — it is just addressable while it exists.
 *
 * `replace`, so Back returns to wherever the operator came from rather than to this
 * route, which would mint a second screen on arrival.
 */
export function MintedScreen({
  input,
  viewId,
  /** Distinguishes one mint from another; a change re-mints. */
  cacheKey,
}: {
  /** A view to CREATE and then open. Ignored when `viewId` is given. */
  input?: ViewInput | null;
  /**
   * A view that already exists, to open a device onto directly.
   *
   * For the built-in quick setups. They are real views with stable ids that the SERVER
   * resolves, so copying one into a temporary view would produce a second, private,
   * immediately-stale duplicate of something everybody already shares — and it would not
   * pick up an edit to the built-in. Opening a device straight onto the built-in id is
   * what the remote does when it points a screen at one, and this is the same act.
   */
  viewId?: string | null;
  cacheKey: string;
}) {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * The screens cap, as a door rather than a sentence. This was the single biggest
   * Premium gate on the site (540 of 669 gate hits in the 30 days to 2026-09-09) and it
   * showed nothing but "Couldn't start that" — no dialog, no button, no way to delete a
   * screen from here. `upsell` remembers the 409 carried the Gold flag; `goldOpen` is the
   * dialog, opened on arrival and re-openable from the sentence once closed.
   */
  const [upsell, setUpsell] = useState(false);
  const [goldOpen, setGoldOpen] = useState(false);
  /*
   * The in-flight mint, cached by key — the PROMISE, not a "have I started" flag.
   *
   * React 18 StrictMode double-invokes effects in dev, so a flag is not enough and is
   * actively worse: run 1 starts the mint, its cleanup marks the closure dead, run 2 sees
   * the flag and returns early, and run 1's result is then discarded by its own liveness
   * check. Nothing ever sets state and the screen hangs on black having already created a
   * view and a device — which is exactly what happened here.
   *
   * Caching the promise makes the second run attach to the FIRST one's result instead of
   * skipping it. One mint, and whichever effect run is alive at settle time gets it.
   */
  const mint = useRef<{ key: string; promise: Promise<string> } | null>(null);

  useEffect(() => {
    if (!input && !viewId) return;
    if (mint.current?.key !== cacheKey) {
      mint.current = {
        key: cacheKey,
        promise: (async () => {
          // An existing view skips the create. Everything after it is identical, which is
          // the point of routing both through here rather than writing a second opener:
          // the StrictMode promise-cache below is the subtle part, and it is easy to get
          // wrong in a copy (see the comment on `mint`).
          const id = viewId ?? (await api.views.createTemp(input!)).view.id;
          const { device } = await api.devices.open({
            viewId: id,
            kind: detectDeviceKind(),
          });
          return device.id;
        })(),
      };
    }
    let live = true;
    mint.current.promise.then(
      (id) => {
        if (live) setDeviceId(id);
      },
      (e: unknown) => {
        if (!live) return;
        mint.current = null; // a failed mint must not be cached, or retry is impossible
        // A screens cap lands here (the mint is a device-open). The gate hit is the
        // funnel's first step; the dialog it opens reports itself as the second.
        if (apiErrorOf(e)?.goldUpsell) {
          trackGoldGate("screens");
          setUpsell(true);
          setGoldOpen(true);
        }
        setError(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      live = false;
    };
  }, [input, viewId, cacheKey]);

  // The site bar rides the mint too — the working black and the failure both. The tab is
  // chrome-to-chrome claimable from the collection page through to the screen it becomes;
  // a bar that blinked out for the black frame would read as the bubble "stopping". The
  // ambient chrome is the right kind here: no device exists yet for a code to adopt.
  if (error) {
    return (
      <div className="flex h-screen w-screen flex-col bg-[var(--color-ink)]">
        <SiteChrome />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="text-lg text-zinc-300">Couldn&rsquo;t start that</span>
          <span className="max-w-md text-sm text-zinc-500">{error}</span>
          {upsell ? (
            <button
              type="button"
              onClick={() => setGoldOpen(true)}
              className="mt-2 rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-black"
            >
              See Premium
            </button>
          ) : null}
        </div>
        {goldOpen ? <GoldDialog source="screens" onClose={() => setGoldOpen(false)} /> : null}
      </div>
    );
  }
  if (!deviceId)
    return (
      <div className="flex h-screen w-screen flex-col bg-[var(--color-ink)]">
        <SiteChrome />
      </div>
    );
  return <Navigate to={`/view/d/${deviceId}`} replace />;
}
